import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Play, Square, Settings, Activity, Cpu, Database, AlertTriangle, CheckCircle } from 'lucide-react';
import { useClient } from '@/contexts/ClientContext';

interface CPUMetrics {
    timestamp: string;
    totalUtilization: number;
    coreUtilization: Array<{ core: number; utilization: number }>;
    maskedCores: number[];
    stressedCores: number[];
    isStressActive: boolean;
}

interface StressTestConfig {
    targetCores: number[];
    allowedCores: number[];
    intensity: number;
    pattern: 'constant' | 'burst' | 'gradual';
    duration: number;
    sqlInstance: string;
}


// Expanded CPUProfile to match backend VMResources/VMCpuInfo structure
interface CPUProfile {
    fqdn: string;
    totalCores: number;
    totalLogicalProcessors: number;
    availableCores: number[];
    currentTotalCPU: number;
    processorName?: string;
    numberOfCores?: number;
    numberOfLogicalProcessors?: number;
    maxClockSpeed?: number;
    currentClockSpeed?: number;
    cpuUsagePercent?: number;
    loadPercentage?: number;
    message?: string;
    cpuInfo?: {
        physicalCores?: number;
        logicalCores?: number;
        currentUsagePercent?: number;
        processorName?: string;
        architecture?: string;
        manufacturer?: string;
        family?: string;
        model?: string;
        stepping?: string;
        maxClockSpeed?: number;
        currentClockSpeed?: number;
        affinityGroups?: any[];
        coreDetails?: any[];
        cacheInfo?: any;
        supportedFeatures?: string[];
        virtualizationSupported?: boolean;
        hyperThreadingEnabled?: boolean;
    };
    collectedAt?: string;
    usedMethod?: string;
}

export const CPUStressDashboard: React.FC = () => {
    const { selectedClient } = useClient();
    const [cpuProfile, setCpuProfile] = useState<CPUProfile | null>(null);
    const [currentMetrics, setCurrentMetrics] = useState<CPUMetrics | null>(null);
    const [metricsHistory, setMetricsHistory] = useState<CPUMetrics[]>([]);
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [testConfig, setTestConfig] = useState<StressTestConfig>({
        targetCores: [0, 1],
        allowedCores: [4, 5, 6, 7],
        intensity: 80,
        pattern: 'constant',
        duration: 300,
        sqlInstance: 'MSSQLSERVER'
    });
    const [targetFQDN, setTargetFQDN] = useState(() => {
        // Use client-specific FQDN if available, otherwise default to localhost
        return selectedClient ? `${selectedClient.code.toLowerCase()}-sql.${selectedClient.name.toLowerCase().replace(/\s+/g, '')}.com` : 'localhost';
    });
    const [sessionId, setSessionId] = useState('');
    const [alerts, setAlerts] = useState<Array<{ type: 'success' | 'warning' | 'error'; message: string }>>([]);

    // Update FQDN when selected client changes
    useEffect(() => {
        if (selectedClient) {
            const clientFQDN = `${selectedClient.code.toLowerCase()}-sql.${selectedClient.name.toLowerCase().replace(/\s+/g, '')}.com`;
            setTargetFQDN(clientFQDN);
        }
    }, [selectedClient]);

    // Real-time metrics updates from API
    useEffect(() => {
        if (!sessionId) return;
        
        const interval = setInterval(async () => {
            try {
                const response = await fetch(`/api/cpu-stress/sessions/${sessionId}/metrics`);
                if (response.ok) {
                    const metrics: CPUMetrics = await response.json();
                    setCurrentMetrics(metrics);
                    setMetricsHistory(prev => [...prev.slice(-29), metrics]); // Keep last 30 points
                    
                    // Update running status based on API response
                    setIsTestRunning(metrics.isStressActive);
                }
            } catch (error) {
                console.error('Failed to fetch metrics:', error);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [sessionId]);

    const handleDiscoverCPU = async () => {
        try {
            console.log('Discovering CPU profile for:', targetFQDN);
            console.log('API call will go to .NET API on port 9000 via proxy');
            
            const response = await fetch('/api/cpu-stress/discover', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fqdn: targetFQDN }),
            });
            
            console.log('CPU discovery response status:', response.status);
            console.log('Response from .NET API:', response.headers.get('content-type'));
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                console.error('CPU discovery failed:', errorData);
                throw new Error(errorData.error || 'Failed to discover CPU profile');
            }
            
            const profile: CPUProfile = await response.json();
            console.log('CPU profile discovered:', profile);
            setCpuProfile(profile);
            addAlert('success', `CPU profile discovered for ${targetFQDN}: ${profile.totalCores} cores, ${profile.totalLogicalProcessors} logical processors`);
        } catch (error) {
            console.error('CPU discovery error:', error);
            addAlert('error', `Failed to discover CPU profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const handleStartStressTest = async () => {
        try {
            const response = await fetch('/api/cpu-stress/start', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(testConfig),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to start stress test');
            }
            
            const result = await response.json();
            setSessionId(result.sessionId);
            setIsTestRunning(true);
            addAlert('success', result.message);
        } catch (error) {
            addAlert('error', error instanceof Error ? error.message : 'Failed to start stress test');
        }
    };

    const handleStopStressTest = async () => {
        if (!sessionId) return;
        
        try {
            const response = await fetch(`/api/cpu-stress/sessions/${sessionId}/stop`, {
                method: 'POST',
            });
            
            if (!response.ok) {
                throw new Error('Failed to stop stress test');
            }
            
            const result = await response.json();
            setIsTestRunning(false);
            addAlert('success', result.message);
        } catch (error) {
            addAlert('error', 'Failed to stop stress test');
        }
    };

    const handleApplyCPUMask = async () => {
        try {
            const response = await fetch('/api/cpu-stress/masking/apply', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    processIds: [1234, 5678], // Mock SQL Server process IDs
                    allowedCores: testConfig.allowedCores,
                    maskingStrategy: 'affinity'
                }),
            });
            
            if (!response.ok) {
                throw new Error('Failed to apply CPU masking');
            }
            
            const result = await response.json();
            addAlert('success', result.message);
        } catch (error) {
            addAlert('error', 'Failed to apply CPU masking');
        }
    };

    const addAlert = (type: 'success' | 'warning' | 'error', message: string) => {
        const newAlert = { type, message };
        setAlerts(prev => [newAlert, ...prev.slice(0, 4)]); // Keep last 5 alerts
        setTimeout(() => {
            setAlerts(prev => prev.filter(alert => alert !== newAlert));
        }, 5000);
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            {/* Client Context Header */}
            {selectedClient && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
                    <div className="flex items-center gap-4">
                        {selectedClient.logoBase64 || selectedClient.logoUrl ? (
                            <img
                                src={selectedClient.logoBase64 || selectedClient.logoUrl}
                                alt={`${selectedClient.name} Logo`}
                                className="w-12 h-12 object-contain border border-gray-200 rounded-lg bg-white p-1"
                            />
                        ) : (
                            <div className="w-12 h-12 bg-shiftleft-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                                {selectedClient.code.charAt(0)}
                            </div>
                        )}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">{selectedClient.name}</h2>
                            <p className="text-sm text-gray-600">
                                {selectedClient.warehouseSystem} • {selectedClient.databaseDialect} • {selectedClient.location}
                            </p>
                        </div>
                        <div className="ml-auto">
                            <Badge variant="outline" className="text-xs">
                                Client: {selectedClient.code}
                            </Badge>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-gray-900">
                    CPU Stress Testing Dashboard
                    {selectedClient && <span className="text-xl text-gray-600 ml-2">- {selectedClient.name}</span>}
                </h1>
                <Badge variant={isTestRunning ? "destructive" : "secondary"} className="text-sm">
                    {isTestRunning ? "Test Running" : "Ready"}
                </Badge>
            </div>

            {/* Alerts */}
            <div className="space-y-2">
                {alerts.map((alert, index) => (
                    <Alert key={index} variant={alert.type === 'error' ? 'destructive' : 'default'}>
                        {alert.type === 'success' && <CheckCircle className="h-4 w-4" />}
                        {alert.type === 'error' && <AlertTriangle className="h-4 w-4" />}
                        <AlertDescription>{alert.message}</AlertDescription>
                    </Alert>
                ))}
            </div>

            {/* Control Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Discovery & Connection */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Database className="h-5 w-5" />
                            Target System
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="fqdn">Server FQDN</Label>
                            <Input
                                id="fqdn"
                                value={targetFQDN}
                                onChange={(e) => setTargetFQDN(e.target.value)}
                                placeholder="sql-server.costco.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Button onClick={handleDiscoverCPU} className="w-full">
                                <Activity className="h-4 w-4 mr-2" />
                                Discover CPU Profile
                            </Button>
                            <Button 
                                onClick={async () => {
                                    try {
                                        console.log('Testing .NET API connection on port 9000...');
                                        const response = await fetch('/api/cpu-stress/test');
                                        
                                        if (response.ok) {
                                            const data = await response.json();
                                            console.log('.NET API test response:', data);
                                            addAlert('success', `.NET API test successful: ${data.message}`);
                                        } else {
                                            const errorText = await response.text();
                                            console.log('.NET API error response:', errorText.substring(0, 200));
                                            addAlert('error', `.NET API test failed: ${response.status}`);
                                        }
                                    } catch (error) {
                                        console.error('.NET API test failed:', error);
                                        addAlert('error', '.NET API connection failed - Is the .NET server running on port 9000?');
                                    }
                                }}
                                variant="outline" 
                                className="w-full"
                            >
                                Test .NET API (Port 9000)
                            </Button>
                        </div>
                        {cpuProfile && (
                            <div className="text-sm space-y-1 p-3 bg-gray-50 rounded">
                                <div><strong>FQDN:</strong> {cpuProfile.fqdn}</div>
                                <div><strong>Total Cores:</strong> {cpuProfile.totalCores}</div>
                                <div><strong>Total Logical Processors:</strong> {cpuProfile.totalLogicalProcessors}</div>
                                <div><strong>Available Cores:</strong> {cpuProfile.availableCores?.join(', ')}</div>
                                <div><strong>Current Total CPU:</strong> {cpuProfile.currentTotalCPU?.toFixed(1)}%</div>
                                {cpuProfile.processorName && <div><strong>Processor Name:</strong> {cpuProfile.processorName}</div>}
                                {cpuProfile.numberOfCores !== undefined && <div><strong>Number of Cores:</strong> {cpuProfile.numberOfCores}</div>}
                                {cpuProfile.numberOfLogicalProcessors !== undefined && <div><strong>Number of Logical Processors:</strong> {cpuProfile.numberOfLogicalProcessors}</div>}
                                {cpuProfile.maxClockSpeed !== undefined && <div><strong>Max Clock Speed:</strong> {cpuProfile.maxClockSpeed} MHz</div>}
                                {cpuProfile.currentClockSpeed !== undefined && <div><strong>Current Clock Speed:</strong> {cpuProfile.currentClockSpeed} MHz</div>}
                                {cpuProfile.cpuUsagePercent !== undefined && <div><strong>CPU Usage Percent:</strong> {cpuProfile.cpuUsagePercent}%</div>}
                                {cpuProfile.loadPercentage !== undefined && <div><strong>Load Percentage:</strong> {cpuProfile.loadPercentage}%</div>}
                                {cpuProfile.message && <div><strong>Message:</strong> {cpuProfile.message}</div>}
                                {cpuProfile.collectedAt && <div><strong>Collected At:</strong> {new Date(cpuProfile.collectedAt).toLocaleString()}</div>}
                                {cpuProfile.usedMethod && <div><strong>Used Method:</strong> {cpuProfile.usedMethod}</div>}
                                {/* Nested CPU Info */}
                                {cpuProfile.cpuInfo && (
                                    <details className="mt-2">
                                        <summary className="font-semibold cursor-pointer">Advanced CPU Info</summary>
                                        <div className="ml-4 space-y-1">
                                            {cpuProfile.cpuInfo.processorName && <div><strong>Processor Name:</strong> {cpuProfile.cpuInfo.processorName}</div>}
                                            {cpuProfile.cpuInfo.architecture && <div><strong>Architecture:</strong> {cpuProfile.cpuInfo.architecture}</div>}
                                            {cpuProfile.cpuInfo.manufacturer && <div><strong>Manufacturer:</strong> {cpuProfile.cpuInfo.manufacturer}</div>}
                                            {cpuProfile.cpuInfo.family && <div><strong>Family:</strong> {cpuProfile.cpuInfo.family}</div>}
                                            {cpuProfile.cpuInfo.model && <div><strong>Model:</strong> {cpuProfile.cpuInfo.model}</div>}
                                            {cpuProfile.cpuInfo.stepping && <div><strong>Stepping:</strong> {cpuProfile.cpuInfo.stepping}</div>}
                                            {cpuProfile.cpuInfo.maxClockSpeed !== undefined && <div><strong>Max Clock Speed:</strong> {cpuProfile.cpuInfo.maxClockSpeed} MHz</div>}
                                            {cpuProfile.cpuInfo.currentClockSpeed !== undefined && <div><strong>Current Clock Speed:</strong> {cpuProfile.cpuInfo.currentClockSpeed} MHz</div>}
                                            {cpuProfile.cpuInfo.physicalCores !== undefined && <div><strong>Physical Cores:</strong> {cpuProfile.cpuInfo.physicalCores}</div>}
                                            {cpuProfile.cpuInfo.logicalCores !== undefined && <div><strong>Logical Cores:</strong> {cpuProfile.cpuInfo.logicalCores}</div>}
                                            {cpuProfile.cpuInfo.currentUsagePercent !== undefined && <div><strong>Current Usage Percent:</strong> {cpuProfile.cpuInfo.currentUsagePercent}%</div>}
                                            {cpuProfile.cpuInfo.supportedFeatures && cpuProfile.cpuInfo.supportedFeatures.length > 0 && (
                                                <div><strong>Supported Features:</strong> {cpuProfile.cpuInfo.supportedFeatures.join(', ')}</div>
                                            )}
                                            {cpuProfile.cpuInfo.virtualizationSupported !== undefined && <div><strong>Virtualization Supported:</strong> {cpuProfile.cpuInfo.virtualizationSupported ? 'Yes' : 'No'}</div>}
                                            {cpuProfile.cpuInfo.hyperThreadingEnabled !== undefined && <div><strong>HyperThreading Enabled:</strong> {cpuProfile.cpuInfo.hyperThreadingEnabled ? 'Yes' : 'No'}</div>}
                                            {/* Cache Info */}
                                            {cpuProfile.cpuInfo.cacheInfo && (
                                                <details className="mt-2">
                                                    <summary className="font-semibold cursor-pointer">Cache Info</summary>
                                                    <div className="ml-4 space-y-1">
                                                        {Object.entries(cpuProfile.cpuInfo.cacheInfo).map(([key, value]) => (
                                                            <div key={key}><strong>{key}:</strong> {Array.isArray(value) ? JSON.stringify(value) : String(value)}</div>
                                                        ))}
                                                    </div>
                                                </details>
                                            )}
                                            {/* Core Details */}
                                            {cpuProfile.cpuInfo.coreDetails && cpuProfile.cpuInfo.coreDetails.length > 0 && (
                                                <details className="mt-2">
                                                    <summary className="font-semibold cursor-pointer">Core Details</summary>
                                                    <div className="ml-4 space-y-1">
                                                        {cpuProfile.cpuInfo.coreDetails.map((core: any, idx: number) => (
                                                            <div key={idx} className="border-b border-gray-200 pb-1 mb-1">
                                                                {Object.entries(core).map(([k, v]) => (
                                                                    <div key={k}><strong>{k}:</strong> {Array.isArray(v) ? JSON.stringify(v) : String(v)}</div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </details>
                                            )}
                                            {/* Affinity Groups */}
                                            {cpuProfile.cpuInfo.affinityGroups && cpuProfile.cpuInfo.affinityGroups.length > 0 && (
                                                <details className="mt-2">
                                                    <summary className="font-semibold cursor-pointer">Affinity Groups</summary>
                                                    <div className="ml-4 space-y-1">
                                                        {cpuProfile.cpuInfo.affinityGroups.map((group: any, idx: number) => (
                                                            <div key={idx} className="border-b border-gray-200 pb-1 mb-1">
                                                                {Object.entries(group).map(([k, v]) => (
                                                                    <div key={k}><strong>{k}:</strong> {Array.isArray(v) ? JSON.stringify(v) : String(v)}</div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </details>
                                            )}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Stress Test Configuration */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Settings className="h-5 w-5" />
                            Stress Test Config
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Target Cores (comma-separated)</Label>
                            <Input
                                value={testConfig.targetCores.join(', ')}
                                onChange={(e) => setTestConfig(prev => ({
                                    ...prev,
                                    targetCores: e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n))
                                }))}
                                placeholder="0, 1, 2"
                            />
                        </div>
                        <div>
                            <Label>Intensity (%)</Label>
                            <Input
                                type="number"
                                min="10"
                                max="100"
                                value={testConfig.intensity}
                                onChange={(e) => setTestConfig(prev => ({ ...prev, intensity: parseInt(e.target.value) }))}
                            />
                        </div>
                        <div>
                            <Label>Duration (seconds)</Label>
                            <Input
                                type="number"
                                min="10"
                                max="86400"
                                value={testConfig.duration}
                                onChange={(e) => setTestConfig(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                                placeholder="300 (5 min), 3600 (1 hr), 28800 (8 hrs)"
                            />
                            <div className="text-xs text-gray-500 mt-1">
                                Max: 24 hours (86400 sec). Recommended: 5min-1hr for initial testing
                            </div>
                        </div>
                        <div>
                            <Label>Pattern</Label>
                            <Select value={testConfig.pattern} onValueChange={(value: any) => setTestConfig(prev => ({ ...prev, pattern: value }))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="constant">Constant</SelectItem>
                                    <SelectItem value="burst">Burst</SelectItem>
                                    <SelectItem value="gradual">Gradual</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* CPU Masking Configuration */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Cpu className="h-5 w-5" />
                            CPU Masking
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Allowed Cores for SQL Server</Label>
                            <Input
                                value={testConfig.allowedCores.join(', ')}
                                onChange={(e) => setTestConfig(prev => ({
                                    ...prev,
                                    allowedCores: e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n))
                                }))}
                                placeholder="4, 5, 6, 7"
                            />
                        </div>
                        <div>
                            <Label>SQL Server Instance</Label>
                            <Input
                                value={testConfig.sqlInstance}
                                onChange={(e) => setTestConfig(prev => ({ ...prev, sqlInstance: e.target.value }))}
                                placeholder="MSSQLSERVER"
                            />
                        </div>
                        <Button onClick={handleApplyCPUMask} variant="outline" className="w-full">
                            Apply CPU Masking
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Control Buttons */}
            <div className="flex gap-4 justify-center">
                <Button
                    onClick={handleStartStressTest}
                    disabled={isTestRunning || !cpuProfile}
                    size="lg"
                    className="px-8"
                >
                    <Play className="h-5 w-5 mr-2" />
                    Start Stress Test
                </Button>
                <Button
                    onClick={handleStopStressTest}
                    disabled={!isTestRunning}
                    variant="destructive"
                    size="lg"
                    className="px-8"
                >
                    <Square className="h-5 w-5 mr-2" />
                    Stop Test
                </Button>
            </div>

            {/* Metrics Dashboard */}
            {currentMetrics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Real-time CPU Usage */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Real-time CPU Usage</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span>Total CPU Usage</span>
                                        <span>{currentMetrics.totalUtilization.toFixed(1)}%</span>
                                    </div>
                                    <Progress value={currentMetrics.totalUtilization} className="h-3" />
                                </div>
                                {metricsHistory.length > 0 && (
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={metricsHistory}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleTimeString()} />
                                            <YAxis domain={[0, 100]} />
                                            <Tooltip labelFormatter={(value) => new Date(value).toLocaleTimeString()} />
                                            <Line type="monotone" dataKey="totalUtilization" stroke="#8884d8" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Per-Core Usage */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Per-Core CPU Usage</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={currentMetrics.coreUtilization}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="core" />
                                    <YAxis domain={[0, 100]} />
                                    <Tooltip />
                                    <Bar
                                        dataKey="utilization"
                                        fill="#8884d8"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="flex gap-4 text-sm mt-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-red-500 rounded"></div>
                                    <span>Stressed Cores</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                                    <span>SQL Server Allowed</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-gray-400 rounded"></div>
                                    <span>Other Cores</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Session Information */}
            {sessionId && (
                <Card>
                    <CardHeader>
                        <CardTitle>Session Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <div className="font-medium">Session ID</div>
                                <div className="text-gray-600">{sessionId}</div>
                            </div>
                            <div>
                                <div className="font-medium">Status</div>
                                <Badge variant={isTestRunning ? "destructive" : "secondary"}>
                                    {isTestRunning ? "Running" : "Stopped"}
                                </Badge>
                            </div>
                            <div>
                                <div className="font-medium">Target Cores</div>
                                <div className="text-gray-600">{testConfig.targetCores.join(', ')}</div>
                            </div>
                            <div>
                                <div className="font-medium">Intensity</div>
                                <div className="text-gray-600">{testConfig.intensity}%</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};